import { type OfferDraft } from './offerDraft';

export const DEFAULT_PERIOD_DAYS = { inspection: 7, financingAppraisal: 14 } as const;

/** Intake's contingency-period ceilings, in days; 0 means explicitly waived. */
const PERIOD_LIMIT_DAYS = {
  inspectionPeriodDays: 20,
  appraisalPeriodDays: 20,
  financingPeriodDays: 30,
} as const;

export const isPeriodValid = (
  value: number | null,
  field: keyof typeof PERIOD_LIMIT_DAYS,
): boolean => value != null && value >= 0 && value <= PERIOD_LIMIT_DAYS[field];

const PERIOD_FIELDS = [
  {
    fields: ['inspectionPeriodDays'],
    event: 'SET_INSPECTION_PERIOD_DAYS',
    id: 'inspection-period',
    label: 'Inspection',
    placeholder: `${DEFAULT_PERIOD_DAYS.inspection} days`,
    defaultValue: DEFAULT_PERIOD_DAYS.inspection,
  },
  {
    fields: ['financingPeriodDays', 'appraisalPeriodDays'],
    event: 'SET_FINANCING_APPRAISAL_PERIOD_DAYS',
    id: 'financing-appraisal-period',
    label: 'Financing & appraisal',
    placeholder: `${DEFAULT_PERIOD_DAYS.financingAppraisal} days`,
    defaultValue: DEFAULT_PERIOD_DAYS.financingAppraisal,
  },
] as const;

type ContingencyPeriodInput = Readonly<
  Pick<OfferDraft, 'financingType' | keyof typeof PERIOD_LIMIT_DAYS>
> & {
  fieldViolations: Readonly<Partial<Record<keyof typeof PERIOD_LIMIT_DAYS, string>>>;
};

export const getContingencyPeriods = (input: ContingencyPeriodInput) =>
  PERIOD_FIELDS.filter(
    (period) =>
      input.financingType !== 'cash' || period.event !== 'SET_FINANCING_APPRAISAL_PERIOD_DAYS',
  ).map((period) => {
    const values = period.fields.map((field) => input[field]);
    const mismatched = values.some((value) => value !== values[0]);
    const value = mismatched ? null : values[0];
    const maxDays = Math.min(...period.fields.map((field) => PERIOD_LIMIT_DAYS[field]));
    const errors = [...new Set(period.fields.map((field) => input.fieldViolations[field]))].filter(
      (violation) => violation != null,
    );
    const valid = period.fields.every((field) => isPeriodValid(input[field], field));
    if (mismatched) {
      errors.push('Enter one period for financing and appraisal.');
    }
    return {
      ...period,
      value,
      waived: value === 0,
      maxDays,
      errors,
      validationError: !mismatched && !valid ? `Enter 0 to ${maxDays} days.` : undefined,
    };
  });
